import common from "@/service/common";

export interface StoryboardFrameWsPayload {
    /** 分镜 storyboardId（segmentId * 100 + shotIndex） */
    storyboardId?: number;
    /** 帧类型，如 keyframe */
    frameType?: string;
    /** 图片 URL（AI 临时地址或 OSS 永久地址） */
    url?: string;
    /** 失败时的错误信息 */
    errorMsg?: string;
    /** true 表示 OSS 终态永久 URL */
    ossFinal?: boolean;
}

export interface TaskSocketEvent {
    /** 事件类型。 */
    type: string;
    /** 当前 WebSocket 实例内的事件序号。 */
    eventId?: number;
    /** 业务会话 ID。 */
    sessionId?: string;
    /** 任务业务类型。 */
    taskType?: string;
    /** 对外任务 ID。 */
    taskId?: string;
    /** 任务关联素材视频 ID。 */
    sourceVideoId?: number;
    /** 任务状态。 */
    status?: string;
    /** 当前执行阶段。 */
    phase?: string;
    /** 当前执行进度。 */
    progress?: number;
    /** 已领取次数。 */
    attempt?: number;
    /** 数据库状态版本号。 */
    version?: number;
    /** 失败错误码。 */
    errorCode?: string;
    /** 失败原因。 */
    errorMessage?: string;
    /** 事件产生时间戳。 */
    timestamp?: number;
    /** 业务扩展数据。 */
    payload?: Record<string, unknown>;
}

type TaskSocketHandler = (event: TaskSocketEvent) => void;
type StatusHandler = (connected: boolean) => void;

class TaskSocketClient {
    private socket: WebSocket | null = null;
    private reconnectTimer: number | null = null;
    private heartbeatTimer: number | null = null;
    private reconnectAttempt = 0;
    private lastMessageAt = 0;
    private manualClose = false;
    private subscribers = new Map<string, Set<TaskSocketHandler>>();
    private statusHandlers = new Set<StatusHandler>();
    private _connected = false;
    /** 是否因认证/握手失败（如400）而关闭，用于退避 */
    private authFailed = false;

    /** 获取当前 WebSocket 是否已连接 */
    isConnected(): boolean {
        return this._connected;
    }

    /** 注册连接状态变化回调，返回取消注册函数 */
    onStatusChange(handler: StatusHandler): () => void {
        this.statusHandlers.add(handler);
        return () => this.statusHandlers.delete(handler);
    }

    private setConnected(connected: boolean) {
        if (this._connected === connected) return;
        this._connected = connected;
        this.statusHandlers.forEach(h => h(connected));
    }

    subscribe(sessionId: string, handler: TaskSocketHandler): () => void {
        const key = sessionId || "*";
        if (!this.subscribers.has(key)) {
            this.subscribers.set(key, new Set());
        }
        this.subscribers.get(key)!.add(handler);
        this.connect();
        return () => {
            const handlers = this.subscribers.get(key);
            if (!handlers) return;
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.subscribers.delete(key);
            }
            if (this.subscribers.size === 0) {
                this.close();
            }
        };
    }

    connect() {
        if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
            return;
        }
        const token = common.getToken();
        if (!token || this.subscribers.size === 0) return;

        this.manualClose = false;
        this.authFailed = false;
        try {
            this.socket = new WebSocket(this.buildUrl(token));
            this.socket.onopen = () => {
                this.reconnectAttempt = 0;
                this.lastMessageAt = Date.now();
                this.authFailed = false;
                this.setConnected(true);
                this.startHeartbeat();
            };
            this.socket.onmessage = (evt) => {
                this.lastMessageAt = Date.now();
                if (evt.data === "pong") return;
                this.handleMessage(evt.data);
            };
            this.socket.onerror = () => {
                // WebSocket handshake 失败（如 400/401）时浏览器会触发 onerror，
                // 浏览器自身会打印错误到控制台（无法通过 JS 抑制）。
                // 此处只标记失连，由 onclose 统一处理重连。
                this.setConnected(false);
            };
            this.socket.onclose = (evt) => {
                this.stopHeartbeat();
                this.socket = null;
                this.setConnected(false);
                // wasClean=false + code 1006/1011 通常表示握手被拒（如 400/401/403）
                if (!evt.wasClean && (evt.code === 1006 || evt.code === 1011)) {
                    this.authFailed = true;
                }
                if (!this.manualClose) this.scheduleReconnect();
            };
        } catch {
            this.setConnected(false);
            this.authFailed = true;
            this.scheduleReconnect();
        }
    }

    close() {
        this.manualClose = true;
        this.clearReconnectTimer();
        this.stopHeartbeat();
        this.setConnected(false);
        this.socket?.close();
        this.socket = null;
    }

    private handleMessage(raw: string) {
        let event: TaskSocketEvent | null = null;
        try {
            event = JSON.parse(raw);
        } catch {
            return;
        }
        if (!event) return;
        if (typeof event.eventId === "number") {
            localStorage.setItem("aiCreationTaskLastEventId", String(event.eventId));
        }
        if (
            event.type !== "AI_CREATION_TASK_UPDATED" &&
            event.type !== "BREAKDOWN_TASK_UPDATED" &&
            event.type !== "REVERSE_WORKSHOP_ENTITY_FRAME_UPDATED" &&
            event.type !== "STORYBOARD_REPLICATION_UPDATED"
        ) return;
        const handlers = [
            ...Array.from(this.subscribers.get("*") || []),
            ...Array.from(this.subscribers.get(event.sessionId || "") || []),
        ];
        handlers.forEach((handler) => handler(event!));
    }

    private buildUrl(token: string): string {
        const apiType = common.getEnvVar("VITE_APP_API_TYPE");
        const base = common.getEnvVar("VITE_APP_BASE_URL");
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        let url: string;
        if (apiType === "current") {
            url = `${protocol}//${window.location.hostname}:${base}/ws/task`;
        } else if (/^https?:\/\//i.test(base)) {
            url = base.replace(/^http/i, "ws").replace(/\/$/, "") + "/ws/task";
        } else if (/^wss?:\/\//i.test(base)) {
            url = base.replace(/\/$/, "") + "/ws/task";
        } else {
            url = `${protocol}//${window.location.host}${base.startsWith("/") ? base : `/${base}`}/ws/task`;
        }
        const lastEventId = localStorage.getItem("aiCreationTaskLastEventId") || "";
        const qs = new URLSearchParams({ token });
        if (lastEventId) qs.set("lastEventId", lastEventId);
        return `${url}?${qs.toString()}`;
    }

    private startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatTimer = window.setInterval(() => {
            if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
            if (Date.now() - this.lastMessageAt > 60000) {
                this.socket.close();
                return;
            }
            this.socket.send("ping");
        }, 25000);
    }

    private stopHeartbeat() {
        if (this.heartbeatTimer) {
            window.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private scheduleReconnect() {
        if (this.subscribers.size === 0 || this.manualClose) return;
        this.clearReconnectTimer();
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;
        // 认证失败（如 400 握手被拒）使用更长退避，避免无效重连刷屏
        const normalDelays = [2000, 5000, 15000, 30000, 60000];
        const authFailDelays = [10000, 30000, 60000, 120000];
        const delays = this.authFailed ? authFailDelays : normalDelays;
        const delay = delays[Math.min(this.reconnectAttempt, delays.length - 1)];
        this.reconnectAttempt += 1;
        this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    }

    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}

const taskSocket = new TaskSocketClient();

if (typeof window !== "undefined") {
    window.addEventListener("online", () => taskSocket.connect());
    window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") taskSocket.connect();
    });
}

export default taskSocket;
